// src/contexts/SubscriptionContext.jsx
// Periodic refresh every 15 minutes + visibility check on tab focus

import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionAPI } from '../services/api';

const SubscriptionContext = createContext();

const POLL_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const MIN_REFETCH_MS   =  2 * 60 * 1000; // don't refetch if checked < 2 min ago

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const [subscription,      setSubscription]      = useState(null);
  const [accessStatus,      setAccessStatus]      = useState(null);
  const [daysRemaining,     setDaysRemaining]      = useState(null);
  const [graceDaysRemaining,setGraceDaysRemaining] = useState(null);
  const [loading,           setLoading]            = useState(true);
  const [error,             setError]              = useState(null);

  const lastFetchedAt = useRef(null);  // tracks last successful fetch time
  const intervalRef   = useRef(null);  // holds the setInterval handle

  // ── Core fetch (silent = don't set loading spinner on background refetches) ─
  const fetchSubscription = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await subscriptionAPI.getSubscription();

      if (data.success) {
        setSubscription(data.data.subscription);
        setAccessStatus(data.data.accessStatus);
        setDaysRemaining(data.data.daysRemaining);
        setGraceDaysRemaining(data.data.graceDaysRemaining);
        lastFetchedAt.current = Date.now();
      } else {
        console.warn('[SubscriptionContext] success:false', data);
        setAccessStatus('expired');
      }
    } catch (err) {
      console.error('[SubscriptionContext] fetch error:', err);
      setError(err.message);
      // Fail open on connectivity blip — don't lock out unnecessarily
      if (!lastFetchedAt.current) setAccessStatus('active');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // ── Throttled refetch — skip if checked less than MIN_REFETCH_MS ago ────────
  const refetch = useCallback((silent = false) => {
    const now = Date.now();
    if (lastFetchedAt.current && now - lastFetchedAt.current < MIN_REFETCH_MS) return;
    fetchSubscription(silent);
  }, [fetchSubscription]);

  // ── Initial fetch + periodic interval ───────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      setSubscription(null);
      setAccessStatus(null);
      setDaysRemaining(null);
      setGraceDaysRemaining(null);
      setLoading(false);
      lastFetchedAt.current = null;
      clearInterval(intervalRef.current);
      return;
    }

    // Fetch immediately on login
    fetchSubscription(false);

    // Then every 15 minutes silently in the background
    intervalRef.current = setInterval(() => {
      fetchSubscription(true);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, fetchSubscription]);

  // ── Visibility listener — refetch when broker returns to the tab ────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refetch(true); // silent — no spinner
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, refetch]);

  // ── Derived booleans ─────────────────────────────────────────────────────────
  const isActive       = accessStatus === 'active';
  const isInGrace      = accessStatus === 'grace';
  const isExpired      = accessStatus === 'expired';
  const isSuspended    = accessStatus === 'suspended';
  const isCancelled    = accessStatus === 'cancelled';
  const isLocked       = isExpired || isSuspended || isCancelled;
  const showGraceBanner = isInGrace;

  const value = {
    subscription, accessStatus, daysRemaining, graceDaysRemaining,
    loading, error,
    isActive, isInGrace, isExpired, isSuspended, isCancelled,
    isLocked, showGraceBanner,
    refetch: () => refetch(false), // exposed refetch always shows spinner
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}